import {
  all, call, put, select, take,
} from 'redux-saga/effects';
import {
  compose, get, getOr,
  identity, isArray, map,
  mapValues, orderBy, pickBy,
  reduce, toNumber, toPairs,
} from 'lodash/fp';
import * as adminServices from '../admin/services';
import { FIREBASE_REF } from '../common/constants';
import firebase from '../common/firebase';
import * as commonServices from '../common/services';
import * as errorActions from '../errors/actions';
import * as actions from './actions';

export function* fetchNewsletter() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_NEWSLETTER_REQUEST);

      const { nid, sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const [docRef, storageRef] = yield all([
        call(firebase.firestore.doc, `${FIREBASE_REF}/school-newsletters/${nid}`),
        call(firebase.storage.ref, `${FIREBASE_REF}/schools/${sid}/school-newsletters/${nid}.pdf`),
      ]);

      const [doc, downloadURL] = yield all([
        call([docRef, 'get']),
        call([storageRef, 'getDownloadURL']),
      ]);

      const { data: blob } = yield commonServices.fetchBlob(downloadURL);

      const newsletter = doc.exists ? doc.data() : null;

      yield put(actions.fetchNewsletterSuccess({ newsletter: { ...newsletter, blob } }));
    } catch (error) {
      yield put(actions.fetchNewsletterFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchSurvey() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_SURVEY_REQUEST);

      const { nid, sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const docRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/surveys/${nid}`);

      const doc = yield call([docRef, 'get']);

      const survey = doc.exists ? doc.data() : null;

      yield put(actions.fetchSurveySuccess({ survey }));
    } catch (error) {
      yield put(actions.fetchSurveyFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchStats() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_STATS_REQUEST);

      const { nid, sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const docRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/stats/${nid}`);

      const doc = yield call([docRef, 'get']);

      const stats = doc.exists ? doc.data() : null;

      yield put(actions.fetchStatsSuccess({ stats }));
    } catch (error) {
      yield put(actions.fetchStatsFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchIndividuals() {
  while (true) {
    try {
      const action = yield take(actions.FETCH_INDIVIDUALS_REQUEST);

      const { nid, sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const [individualRef, targetRef] = yield all([
        call(firebase.firestore.doc, `${FIREBASE_REF}/individuals/${nid}`),
        call(firebase.firestore.doc, `${FIREBASE_REF}/targets/${nid}`),
      ]);

      const [individualDoc, targetDoc] = yield all([
        call([individualRef, 'get']),
        call([targetRef, 'get']),
      ]);

      const individuals = individualDoc.exists ? individualDoc.data() : null;
      const targets = targetDoc.exists ? targetDoc.data() : null;

      const newIndividuals = compose(
        orderBy(['target.name', 'updatedAt'], ['asc', 'desc']),
        map(([key, value]) => ({
          id: toNumber(key),
          answers: value.answers,
          signatures: value.signatures,
          updatedAt: Date.parse(value.updatedAt && value.updatedAt.toDate()) || 0,
          target: getOr(null, `data.${key}`, targets),
        })),
        toPairs,
        get('data'),
      )(individuals);

      yield put(actions.fetchIndividualsSuccess({ individuals: newIndividuals }));
    } catch (error) {
      yield put(actions.fetchIndividualsFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* switchActiveStatus() {
  while (true) {
    try {
      const action = yield take(actions.SWITCH_ACTIVE_STATUS_REQUEST);

      const { nid, sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const ref = yield call(firebase.firestore.doc, `${FIREBASE_REF}/school-newsletters/${nid}`);

      const updateFunction = async (transaction) => {
        const doc = await transaction.get(ref);

        if (!doc.exists) {
          throw new Error('Document does not exist.');
        }

        const newsletter = doc.data();

        transaction.update(ref, {
          status: newsletter.status === 'closed' || newsletter.paused
            ? 'ongoing' : 'closed',
        });
      };

      yield call(firebase.firestore.runTransaction, updateFunction);

      yield put({
        type: actions.SWITCH_ACTIVE_STATUS_SUCCESS,
      });
    } catch (error) {
      yield put({
        type: actions.SWITCH_ACTIVE_STATUS_FAILURE,
        payload: { error },
      });

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* updateQuestionTitle() {
  while (true) {
    try {
      const action = yield take(actions.UPDATE_QUESTION_TITLE_REQUEST);
      const survey = yield select(state => state.newsletter.survey);

      const { nid, sid } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const ref = yield call(firebase.firestore.doc, `${FIREBASE_REF}/surveys/${nid}`);

      yield call(
        firebase.firestore.updateDocument,
        ref,
        { questions: survey.data.questions },
      );


      yield put(actions.updateQuestionTitleSuccess());
    } catch (error) {
      const survey = yield select(state => state.newsletter.survey);

      const number = get('number', survey.data);
      const prevValue = get(`questions[${number - 1}].prevValue`, survey.data);

      yield put(actions.updateQuestionTitleFailure({ error, number, prevValue }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* deleteIndividualResponse() {
  while (true) {
    try {
      const action = yield take(actions.DELETE_INDIVIDUAL_RESPONSE_REQUEST);

      const {
        answers, gcid, nid, sid,
      } = action.payload;

      yield call(adminServices.fetchSchoolAdminRelationships, { sid });

      const newsletterRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/school-newsletters/${nid}`);
      const statsRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/stats/${nid}`);

      const updateFunction = {
        newsletter: async (transaction) => {
          const doc = await transaction.get(newsletterRef);

          if (!doc.exists) {
            throw new Error('Document does not exist.');
          }

          const newsletter = doc.data();

          transaction.update(newsletterRef, {
            recordCount: newsletter.recordCount - 1,
          });
        },
        stats: async (transaction) => {
          const doc = await transaction.get(statsRef);

          if (!doc.exists) {
            throw new Error('Document does not exist.');
          }

          const stats = doc.data();

          const newStats = mapValues.convert({ cap: false })((value, key) => pickBy(identity, (
            isArray(answers[key])
              ? {
                ...value,
                ...reduce((acc, cur) => ({
                  ...acc,
                  [cur]: value[cur] - 1,
                }), {}, answers[key]),
              }
              : {
                ...value,
                [answers[key]]: value[answers[key]] - 1,
              }
          )), stats);

          transaction.update(statsRef, newStats);
        },
      };

      const deleteField = yield call(firebase.firestore.FieldValue.delete);

      const individualRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/individuals/${nid}`);
      const targetRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/targets/${nid}`);

      yield all([
        call(firebase.firestore.runTransaction, updateFunction.newsletter),
        call(firebase.firestore.runTransaction, updateFunction.stats),
        call(firebase.firestore.updateDocument, individualRef, {
          [`data.${gcid}`]: deleteField,
        }),
        call(firebase.firestore.updateDocument, targetRef, {
          [`data.${gcid}.recorded`]: false,
        }),
      ]);

      yield put(actions.deleteIndividualResponseSuccess({ gcid }));
    } catch (error) {
      yield put(actions.deleteIndividualResponseFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}
